import { useCallback, useSyncExternalStore } from 'react';

const KEY_CHARACTER_ID = 'mythkit_last_character_id';
const KEY_CHARACTER_NAME = 'mythkit_last_character_name';
const KEY_CAMPAIGN_ID = 'mythkit_last_campaign_id';
const KEY_CAMPAIGN_NAME = 'mythkit_last_campaign_name';
const KEY_CAMPAIGN_OPENED_AT = 'mythkit_last_campaign_opened_at';

interface LastCharacter {
  id: string;
  name: string;
}

interface LastCampaign {
  id: string;
  name: string;
  openedAt: string | null;
}

function getStoredCharacter(): LastCharacter | null {
  try {
    const id = localStorage.getItem(KEY_CHARACTER_ID);
    const name = localStorage.getItem(KEY_CHARACTER_NAME);
    if (id && name) return { id, name };
  } catch {
    // ignore
  }
  return null;
}

function getStoredCampaign(): LastCampaign | null {
  try {
    const id = localStorage.getItem(KEY_CAMPAIGN_ID);
    const name = localStorage.getItem(KEY_CAMPAIGN_NAME);
    if (id && name) {
      return {
        id,
        name,
        openedAt: localStorage.getItem(KEY_CAMPAIGN_OPENED_AT),
      };
    }
  } catch {
    // ignore
  }
  return null;
}

let cachedSnapshot: { lastCharacter: LastCharacter | null; lastCampaign: LastCampaign | null } = {
  lastCharacter: null,
  lastCampaign: null,
};
let cachedSerialized = '';

function getSnapshot(): { lastCharacter: LastCharacter | null; lastCampaign: LastCampaign | null } {
  const char = getStoredCharacter();
  const camp = getStoredCampaign();
  const serialized = JSON.stringify({
    c: char?.id ?? '',
    n: char?.name ?? '',
    cid: camp?.id ?? '',
    cname: camp?.name ?? '',
    opened: camp?.openedAt ?? '',
  });
  if (serialized === cachedSerialized) {
    return cachedSnapshot;
  }
  cachedSerialized = serialized;
  cachedSnapshot = { lastCharacter: char, lastCampaign: camp };
  return cachedSnapshot;
}

function subscribe(callback: () => void): () => void {
  const handler = () => callback();
  window.addEventListener('storage', handler);
  window.addEventListener('mythkit_last_visited_change', handler);
  return () => {
    window.removeEventListener('storage', handler);
    window.removeEventListener('mythkit_last_visited_change', handler);
  };
}

export function useLastVisited(): {
  lastCharacter: LastCharacter | null;
  lastCampaign: LastCampaign | null;
  setLastCharacter: (id: string, name: string) => void;
  setLastCampaign: (id: string, name: string) => void;
  clearLastCharacter: () => void;
  clearLastCampaign: () => void;
} {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setLastCharacter = useCallback((id: string, name: string) => {
    try {
      localStorage.setItem(KEY_CHARACTER_ID, id);
      localStorage.setItem(KEY_CHARACTER_NAME, name);
      window.dispatchEvent(new Event('mythkit_last_visited_change'));
    } catch {
      // ignore
    }
  }, []);

  const setLastCampaign = useCallback((id: string, name: string) => {
    try {
      localStorage.setItem(KEY_CAMPAIGN_ID, id);
      localStorage.setItem(KEY_CAMPAIGN_NAME, name);
      localStorage.setItem(KEY_CAMPAIGN_OPENED_AT, new Date().toISOString());
      window.dispatchEvent(new Event('mythkit_last_visited_change'));
    } catch {
      // ignore
    }
  }, []);

  const clearLastCharacter = useCallback(() => {
    try {
      localStorage.removeItem(KEY_CHARACTER_ID);
      localStorage.removeItem(KEY_CHARACTER_NAME);
      window.dispatchEvent(new Event('mythkit_last_visited_change'));
    } catch {
      // ignore
    }
  }, []);

  const clearLastCampaign = useCallback(() => {
    try {
      localStorage.removeItem(KEY_CAMPAIGN_ID);
      localStorage.removeItem(KEY_CAMPAIGN_NAME);
      localStorage.removeItem(KEY_CAMPAIGN_OPENED_AT);
      window.dispatchEvent(new Event('mythkit_last_visited_change'));
    } catch {
      // ignore
    }
  }, []);

  return {
    lastCharacter: snapshot.lastCharacter,
    lastCampaign: snapshot.lastCampaign,
    setLastCharacter,
    setLastCampaign,
    clearLastCharacter,
    clearLastCampaign,
  };
}
